/**
 * Ce stie un bloc despre pagina in care sta.
 *
 * Blocurile NU cheama direct contextul cosului sau navigarea: primesc de sus
 * exact actiunile pe care au voie sa le declanseze. Motivul e concret — aceleasi
 * blocuri se randeaza si pe pagina de start, si pe paginile in plus, iar acolo
 * unde adresa nu are fereastra deschisa (o pagina „Despre") un bloc care ar
 * chema-o singur ar arunca la randare.
 */

import type { MarketPageBlock } from "@deliveryapp/shared";

export interface BlockContext {
  /** Deschide fereastra de adresa. */
  requestAddress: () => void;
  /** Adresa din sesiune, gata de afisat. `null` = nu s-a introdus niciuna. */
  addressLabel: string | null;
  /** Deschide vitrina cu slug-ul dat (selecteaza + navigheaza). */
  openStorefront: (slug: string) => void;
  /** Textele pietei, cu rezerva in romana. */
  t: (key: string, fallback: string) => string;
}

export interface BlockProps {
  block: MarketPageBlock;
  ctx: BlockContext;
  /**
   * `1` pentru primul bloc cu titlu al paginii. Fiecare pagina trebuie sa aiba
   * exact un `h1`: cititoarele de ecran folosesc ierarhia de titluri ca sa
   * navigheze, iar o pagina care incepe cu `h2` nu are punct de intrare.
   */
  headingLevel: 1 | 2;
}

/**
 * Ce se accepta intr-un `href` venit din config.
 *
 * Core-ul valideaza deja aceeasi regula la PUBLICARE. Verificarea se repeta aici
 * si nu e redundanta: configurile publicate inainte de acea validare exista deja
 * in baza si se servesc mai departe, iar `javascript:` intr-un `href` inseamna
 * cod rulat in pagina pietei, sub domeniul ei, pentru fiecare vizitator. Cel mai
 * ieftin loc in care se poate opri e chiar inainte de randare.
 */
const SAFE_HREF_RE = /^(?:https?:\/\/[^\s]+|\/(?![/\\])[^\s]*|#[^\s]*|mailto:[^\s]+|tel:[^\s]+)$/i;

export type ResolvedHref =
  | { kind: "intern"; to: string }
  | { kind: "extern"; href: string }
  | null;

/**
 * Traduce o legatura din config in ceva randabil. `null` inseamna „nu se
 * afiseaza butonul" — nu „se afiseaza butonul, dar nu face nimic": un buton
 * mort e mai rau decat un buton lipsa, fiindca cere o apasare si nu raspunde.
 */
export function resolveHref(raw: string): ResolvedHref {
  const value = raw.trim();
  if (value.length === 0 || !SAFE_HREF_RE.test(value)) return null;
  // Ancorele si caile interne raman in aplicatie (fara reincarcare completa);
  // `mailto:`/`tel:` si adresele complete pleaca din ea.
  if (value.startsWith("/") || value.startsWith("#")) return { kind: "intern", to: value };
  return { kind: "extern", href: value };
}

/**
 * Imaginile: aceeasi grija, alta lista. `data:` nu se accepta — o imagine
 * inline ar fi trecut prin configul publicat, l-ar fi umflat si ar fi ajuns in
 * fiecare raspuns de bootstrap.
 */
const SAFE_IMAGE_RE = /^(?:https?:\/\/[^\s]+|\/(?![/\\])[^\s]*)$/i;

export function safeImage(raw: string): string | null {
  const value = raw.trim();
  return value.length > 0 && SAFE_IMAGE_RE.test(value) ? value : null;
}

/** Paragrafele se despart prin rand gol, ca intr-un camp de text obisnuit. */
export function paragraphs(text: string): string[] {
  return text
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** Numarul de coloane, ca lista de clase Tailwind (nu se poate construi dinamic). */
export const COLUMN_CLASSES: Record<string, string> = {
  "2": "sm:grid-cols-2",
  "3": "sm:grid-cols-2 lg:grid-cols-3",
  "4": "sm:grid-cols-2 lg:grid-cols-4",
};

export function columnClass(value: string): string {
  return COLUMN_CLASSES[value] ?? COLUMN_CLASSES["2"] ?? "sm:grid-cols-2";
}
